import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatMoney } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import {
  ShieldCheck,
  Users,
  ArrowDown,
  ArrowUp,
  Coins,
  ListChecks,
  Vault,
  ClockCounterClockwise,
  X,
  Check,
  MagnifyingGlass,
  DownloadSimple,
  Key,
  PauseCircle,
  PlayCircle,
  NotePencil,
  Copy,
  UserCircle,
  ChartLineUp,
  Warning,
  CreditCard,
  Plus,
  Trash,
  Bank,
  CurrencyBtc,
  FileText,
} from "@phosphor-icons/react";

const TABS = [
  { key: "overview", label: "Overview", icon: Vault },
  { key: "investors", label: "Investors", icon: Users },
  { key: "deposits", label: "Deposits", icon: ArrowDown },
  { key: "withdrawals", label: "Withdrawals", icon: ArrowUp },
  { key: "transactions", label: "Transactions", icon: Coins },
  { key: "balances", label: "Balances", icon: ListChecks },
  { key: "payments", label: "Payment Methods", icon: CreditCard },
  { key: "usdf", label: "USDF Declarations", icon: FileText },
  { key: "kyc", label: "KYC", icon: ShieldCheck },
  { key: "audit", label: "Audit Logs", icon: ClockCounterClockwise },
];

const STATUS_TONE = {
  pending: "text-amber-400 border-amber-400/40",
  completed: "text-emerald-400 border-emerald-400/40",
  rejected: "text-red-400 border-red-400/40",
  approved: "text-emerald-400 border-emerald-400/40",
  active: "text-emerald-400 border-emerald-400/40",
  suspended: "text-red-400 border-red-400/40",
  not_submitted: "text-neutral-400 border-white/15",
  received: "text-sky-400 border-sky-400/40",
  under_review: "text-amber-400 border-amber-400/40",
  verified: "text-emerald-400 border-emerald-400/40",
};

const METHOD_LABEL = {
  bank_wire: "Bank Wire",
  btc: "Bitcoin",
  usdt: "USDT ERC-20",
};

const ROLE_LABEL = {
  super_admin: "Super Admin",
  finance_admin: "Finance Admin",
  support_admin: "Support Admin",
  user: "Investor",
};

const FINANCE_ROLES = ["super_admin", "finance_admin"];

function useCan(user) {
  return useMemo(() => ({
    approveTx: FINANCE_ROLES.includes(user?.role),
    manageUsers: ["super_admin", "finance_admin", "support_admin"].includes(user?.role),
    export: FINANCE_ROLES.includes(user?.role),
  }), [user]);
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [openInvestorId, setOpenInvestorId] = useState(null);

  if (!user || !["super_admin", "finance_admin", "support_admin"].includes(user.role)) {
    return (
      <div className="max-w-xl mx-auto p-16 text-center text-neutral-400">
        <ShieldCheck size={40} weight="duotone" className="text-[#E6C998] mx-auto mb-4" />
        <div className="font-display text-2xl mb-2 text-white">Restricted</div>
        <div className="text-sm">
          The admin console is available to Madrefajona desk staff only.
        </div>
        <Link to="/dashboard" className="btn-outline-gold inline-block mt-6 px-6 py-3 text-sm">
          Return to dashboard
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="text-white min-h-[80vh]">
      <section className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
          <div className="overline mb-2">{ROLE_LABEL[user.role]} · Admin Console</div>
          <h1 className="font-display text-3xl sm:text-4xl flex items-center gap-3">
            <ShieldCheck size={30} weight="duotone" className="text-[#E6C998]" />
            Madrefajona Desk
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 pt-8">
        <aside className="border border-white/10 bg-[#0A0A0A] p-2 h-fit sticky top-24 flex lg:flex-col overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-3 px-4 py-3 text-sm text-left whitespace-nowrap transition-colors ${
                tab === t.key
                  ? "bg-[#141414] text-[#E6C998] border-l-2 border-[#E6C998]"
                  : "text-neutral-400 hover:text-white border-l-2 border-transparent"
              }`}
            >
              <t.icon size={18} weight="duotone" />
              {t.label}
            </button>
          ))}
        </aside>

        <div className="pb-16">
          {tab === "overview" && <OverviewPanel onOpenInvestor={setOpenInvestorId} />}
          {tab === "investors" && <InvestorsPanel onOpenInvestor={setOpenInvestorId} />}
          {tab === "deposits" && <DepositsPanel onOpenInvestor={setOpenInvestorId} />}
          {tab === "withdrawals" && <WithdrawalsPanel onOpenInvestor={setOpenInvestorId} />}
          {tab === "transactions" && <TransactionsPanel />}
          {tab === "balances" && <BalancesPanel onOpenInvestor={setOpenInvestorId} />}
          {tab === "payments" && <PaymentMethodsPanel />}
          {tab === "usdf" && <UsdfPanel />}
          {tab === "kyc" && <KycQueue />}
          {tab === "audit" && <AuditPanel />}
        </div>
      </div>

      {openInvestorId && (
        <InvestorDrawer id={openInvestorId} onClose={() => setOpenInvestorId(null)} />
      )}
    </div>
  );
}

/* ================================ OVERVIEW =============================== */
function OverviewPanel({ onOpenInvestor }) {
  const [s, setS] = useState(null);
  const [recent, setRecent] = useState([]);
  const [pendDep, setPendDep] = useState([]);
  const [pendWd, setPendWd] = useState([]);

  useEffect(() => {
    api.get("/admin/summary").then((r) => setS(r.data));
    api.get("/admin/recent-transactions?limit=10").then((r) => setRecent(r.data));
    api.get("/admin/transactions?type=deposit&status=pending&limit=5").then((r) => setPendDep(r.data));
    api.get("/admin/transactions?type=withdraw&status=pending&limit=5").then((r) => setPendWd(r.data));
  }, []);

  if (!s) return <div className="text-neutral-500 text-sm">Loading…</div>;

  const cards = [
    ["Total Investors", s.total_users, "gold"],
    ["Active Investors", s.active_users, "gold"],
    ["Total Deposits", formatMoney(s.total_deposits), "gold"],
    ["Total Withdrawals", formatMoney(s.total_withdrawals), "gold"],
    ["Assets Under Management", formatMoney(s.aum), "gold"],
    ["Active Investments", s.active_investments, "gold"],
    ["Pending Deposits", s.pending_deposits, "amber"],
    ["Pending Withdrawals", s.pending_withdrawals, "amber"],
  ];

  return (
    <div className="space-y-8">
      <h2 className="font-display text-2xl">Desk overview</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {cards.map(([label, value, tone]) => (
          <div key={label} data-testid={`admin-stat-${label}`} className="border border-white/10 bg-[#141414] p-6">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-3">{label}</div>
            <div className={`font-display text-2xl md:text-3xl num ${tone === "amber" ? "text-amber-400" : "text-[#E6C998]"}`}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PendingActionCard title="Pending deposits" items={pendDep} type="deposit" onOpenInvestor={onOpenInvestor} />
        <PendingActionCard title="Pending withdrawals" items={pendWd} type="withdraw" onOpenInvestor={onOpenInvestor} />
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-display text-xl">Recent transactions</h3>
          <span className="text-xs text-neutral-500">Last {recent.length}</span>
        </div>
        <Table
          head={["When", "Investor", "Type", "Reference", "Amount", "Status"]}
          rows={recent.map((t) => [
            new Date(t.created_at).toLocaleString(),
            <button key="u" className="text-[#E6C998] underline" onClick={() => onOpenInvestor(t.user_id)}>
              {t.user_email}
            </button>,
            <span key="ty" className="capitalize">{t.type}{t.asset_symbol ? ` · ${t.asset_symbol}` : ""}</span>,
            <span key="r" className="text-xs num">{t.reference || "—"}</span>,
            <span key="a" className="num">{formatMoney(t.amount)}</span>,
            <Pill key="s" tone={t.status}>{t.status}</Pill>,
          ])}
        />
      </div>
    </div>
  );
}

function PendingActionCard({ title, items, type, onOpenInvestor }) {
  return (
    <div className="border border-white/10 bg-[#141414] p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">{title}</div>
        <span className="text-xs text-neutral-500 num">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-neutral-500 text-sm py-6">Nothing pending — desk is clear.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((t) => (
            <li key={t.id} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-b-0">
              <div className="min-w-0">
                <button className="text-sm text-white truncate hover:text-[#E6C998]" onClick={() => onOpenInvestor(t.user_id)}>
                  {t.user_email}
                </button>
                <div className="text-xs text-neutral-500 num">{t.reference} · {METHOD_LABEL[t.method] || t.method}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="num text-[#E6C998]">{formatMoney(t.amount)}</div>
                <div className="text-xs text-neutral-500">{new Date(t.created_at).toLocaleDateString()}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ================================ INVESTORS ============================== */
function InvestorsPanel({ onOpenInvestor }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [kycFilter, setKycFilter] = useState("");

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (statusFilter) q.set("status", statusFilter);
    if (kycFilter) q.set("kyc", kycFilter);
    api.get(`/admin/investors?${q.toString()}`).then((r) => setRows(r.data));
  }, [search, statusFilter, kycFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <PanelShell
      title={`Investors · ${rows.length}`}
      right={
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-neutral-500" />
            <input
              data-testid="investor-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, phone, account id…"
              className="bg-black border border-white/20 text-white text-sm pl-9 pr-3 py-2 w-64 focus:border-[#E6C998] focus:outline-none"
            />
          </div>
          <Select value={statusFilter} onChange={setStatusFilter} options={[
            ["", "All accounts"], ["active", "Active"], ["suspended", "Suspended"],
          ]} testid="investor-status-filter" />
          <Select value={kycFilter} onChange={setKycFilter} options={[
            ["", "All KYC"], ["approved", "Verified"], ["pending", "Pending"],
            ["rejected", "Rejected"], ["not_submitted", "Not submitted"],
          ]} testid="investor-kyc-filter" />
        </div>
      }
    >
      <Table
        head={["Account", "Name / Email", "Phone", "Plan", "Status", "KYC", "Balance", "Reserved", "Joined"]}
        rows={rows.map((u) => [
          <button
            key="acc"
            data-testid={`investor-open-${u.id}`}
            className="num text-[#E6C998] hover:underline"
            onClick={() => onOpenInvestor(u.id)}
          >
            {u.account_id}
          </button>,
          <div key="ne"><div>{u.name}</div><div className="text-xs text-neutral-500">{u.email}</div></div>,
          <span key="p" className="num text-xs text-neutral-400">{u.phone || "—"}</span>,
          u.investment_plan || "Standard",
          <Pill key="s" tone={u.account_status}>{u.account_status}</Pill>,
          <Pill key="k" tone={u.kyc_status}>{u.kyc_status}</Pill>,
          <span key="b" className="num">{formatMoney(u.balance || 0)}</span>,
          <span key="r" className="num">{formatMoney(u.reserved_balance || 0)}</span>,
          new Date(u.created_at || 0).toLocaleDateString(),
        ])}
      />
    </PanelShell>
  );
}

/* ================================ DEPOSITS =============================== */
function DepositsPanel({ onOpenInvestor }) {
  const { user } = useAuth();
  const can = useCan(user);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");

  const load = useCallback(() => {
    const q = new URLSearchParams({ type: "deposit" });
    if (status) q.set("status", status);
    api.get(`/admin/transactions?${q.toString()}`).then((r) => setItems(r.data));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (id) => {
    if (!window.confirm("Confirm this deposit? Funds will be credited to the investor.")) return;
    try {
      const { data } = await api.post(`/admin/deposits/${id}/confirm`);
      toast.success(`Credited ${formatMoney(data.credited)}${data.bonus ? ` (bonus ${formatMoney(data.bonus)})` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Rejection reason (visible in audit log):") || null;
    try {
      await api.post(`/admin/deposits/${id}/reject`, { reason });
      toast.success("Deposit rejected");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PanelShell
      title="Deposits"
      right={
        <div className="flex gap-3">
          <FinancialStatusFilter value={status} onChange={setStatus} />
          {can.export && <ExportButton type="deposit" status={status} />}
        </div>
      }
    >
      <Table
        head={["When", "Investor", "Method", "Reference", "Amount", "Bonus", "Status", ""]}
        rows={items.map((t) => [
          new Date(t.created_at).toLocaleString(),
          <button key="u" onClick={() => onOpenInvestor(t.user_id)} className="text-[#E6C998] hover:underline">
            {t.user_email}
          </button>,
          METHOD_LABEL[t.method] || t.method,
          <span key="ref" className="num text-xs">{t.reference}</span>,
          <span key="a" className="num">{formatMoney(t.amount)}</span>,
          <span key="b" className="num text-[#E6C998]">
            {t.bonus ? `+${formatMoney(t.bonus)}` : (t.bonus_preview ? `~${formatMoney(t.bonus_preview)}` : "—")}
          </span>,
          <Pill key="s" tone={t.status}>{t.status}</Pill>,
          t.status === "pending" && can.approveTx ? (
            <div key="ac" className="flex gap-2">
              <button
                data-testid={`admin-deposit-confirm-${t.id}`}
                onClick={() => confirm(t.id)}
                className="text-emerald-400 hover:text-emerald-300 p-1"
                title="Confirm receipt"
              ><Check size={16} weight="bold" /></button>
              <button
                data-testid={`admin-deposit-reject-${t.id}`}
                onClick={() => reject(t.id)}
                className="text-red-400 hover:text-red-300 p-1"
                title="Reject"
              ><X size={16} weight="bold" /></button>
            </div>
          ) : (
            <span key="by" className="text-xs text-neutral-500">
              {t.confirmed_by || t.rejected_by || "—"}
            </span>
          ),
        ])}
      />
    </PanelShell>
  );
}

/* =============================== WITHDRAWALS ============================= */
function WithdrawalsPanel({ onOpenInvestor }) {
  const { user } = useAuth();
  const can = useCan(user);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");

  const load = useCallback(() => {
    const q = new URLSearchParams({ type: "withdraw" });
    if (status) q.set("status", status);
    api.get(`/admin/transactions?${q.toString()}`).then((r) => setItems(r.data));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (!window.confirm("Approve this withdrawal? Reserved funds will leave the platform.")) return;
    try {
      await api.post(`/admin/withdrawals/${id}/approve`);
      toast.success("Withdrawal approved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Rejection reason (visible in audit log):") || null;
    try {
      await api.post(`/admin/withdrawals/${id}/reject`, { reason });
      toast.success("Withdrawal rejected · funds released");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PanelShell
      title="Withdrawals"
      right={
        <div className="flex gap-3">
          <FinancialStatusFilter value={status} onChange={setStatus} />
          {can.export && <ExportButton type="withdraw" status={status} />}
        </div>
      }
    >
      <Table
        head={["When", "Investor", "Method", "Destination", "Reference", "Amount", "Status", ""]}
        rows={items.map((t) => [
          new Date(t.created_at).toLocaleString(),
          <button key="u" onClick={() => onOpenInvestor(t.user_id)} className="text-[#E6C998] hover:underline">
            {t.user_email}
          </button>,
          METHOD_LABEL[t.method] || t.method,
          <span key="d" className="text-xs num inline-block max-w-[220px] truncate align-bottom">
            {t.destination}
            {t.beneficiary_name && <div className="text-neutral-500">{t.beneficiary_name}</div>}
          </span>,
          <span key="ref" className="num text-xs">{t.reference}</span>,
          <span key="a" className="num">{formatMoney(t.amount)}</span>,
          <Pill key="s" tone={t.status}>{t.status}</Pill>,
          t.status === "pending" && can.approveTx ? (
            <div key="ac" className="flex gap-2">
              <button
                data-testid={`admin-withdraw-approve-${t.id}`}
                onClick={() => approve(t.id)}
                className="text-emerald-400 hover:text-emerald-300 p-1"
              ><Check size={16} weight="bold" /></button>
              <button
                data-testid={`admin-withdraw-reject-${t.id}`}
                onClick={() => reject(t.id)}
                className="text-red-400 hover:text-red-300 p-1"
              ><X size={16} weight="bold" /></button>
            </div>
          ) : (
            <span key="by" className="text-xs text-neutral-500">
              {t.approved_by || t.rejected_by || "—"}
            </span>
          ),
        ])}
      />
    </PanelShell>
  );
}

/* ============================== TRANSACTIONS ============================= */
function TransactionsPanel() {
  const { user } = useAuth();
  const can = useCan(user);
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (type) q.set("type", type);
    if (status) q.set("status", status);
    if (search) q.set("search", search);
    api.get(`/admin/transactions?${q.toString()}`).then((r) => setItems(r.data));
  }, [type, status, search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <PanelShell
      title={`All transactions · ${items.length}`}
      right={
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-neutral-500" />
            <input
              data-testid="tx-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Reference, email, account id…"
              className="bg-black border border-white/20 text-white text-sm pl-9 pr-3 py-2 w-64 focus:border-[#E6C998] focus:outline-none"
            />
          </div>
          <Select value={type} onChange={setType} options={[
            ["", "All types"], ["deposit", "Deposits"], ["withdraw", "Withdrawals"], ["invest", "Investments"],
          ]} testid="tx-type-filter" />
          <FinancialStatusFilter value={status} onChange={setStatus} includeAll />
          {can.export && <ExportButton type={type} status={status} />}
        </div>
      }
    >
      <Table
        head={["When", "Investor", "Type", "Reference", "Amount", "Status"]}
        rows={items.map((t) => [
          new Date(t.created_at).toLocaleString(),
          t.user_email,
          <span key="ty" className="capitalize">{t.type}{t.asset_symbol ? ` · ${t.asset_symbol}` : ""}</span>,
          <span key="ref" className="text-xs num">{t.reference || "—"}</span>,
          <span key="a" className="num">{formatMoney(t.amount)}</span>,
          <Pill key="s" tone={t.status}>{t.status}</Pill>,
        ])}
      />
    </PanelShell>
  );
}

/* ============================== BALANCES ============================= */
function BalancesPanel({ onOpenInvestor }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/balances").then((r) => setRows(r.data)); }, []);
  const totalCash = rows.reduce((s, r) => s + (r.balance || 0), 0);
  const totalReserved = rows.reduce((s, r) => s + (r.reserved_balance || 0), 0);
  return (
    <PanelShell
      title="Account balances"
      right={
        <div className="text-xs text-neutral-400 num">
          Total cash <span className="text-[#E6C998]">{formatMoney(totalCash)}</span> · Reserved <span className="text-[#E6C998]">{formatMoney(totalReserved)}</span>
        </div>
      }
    >
      <Table
        head={["Account", "Investor", "Status", "KYC", "Available", "Reserved"]}
        rows={rows.map((u) => [
          <button key="a" onClick={() => onOpenInvestor(u.id)} className="num text-[#E6C998] hover:underline">
            {u.account_id || "—"}
          </button>,
          <div key="n"><div>{u.email}</div><div className="text-xs text-neutral-500">{u.name}</div></div>,
          <Pill key="st" tone={u.account_status}>{u.account_status}</Pill>,
          <Pill key="k" tone={u.kyc_status}>{u.kyc_status}</Pill>,
          <span key="bal" className="num">{formatMoney(u.balance || 0)}</span>,
          <span key="res" className="num">{formatMoney(u.reserved_balance || 0)}</span>,
        ])}
      />
    </PanelShell>
  );
}

/* ================================= KYC ================================= */
function KycQueue() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = () => api.get(`/admin/kyc?status=${status}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]);

  const decide = async (userId, next) => {
    const note = next === "rejected" ? (window.prompt("Reviewer note:") || null) : null;
    try {
      await api.post(`/admin/kyc/${userId}`, { status: next, note });
      toast.success(`KYC ${next}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <PanelShell
      title="KYC review queue"
      right={<Select value={status} onChange={setStatus} options={[
        ["pending", "Pending"], ["approved", "Verified"], ["rejected", "Rejected"],
      ]} testid="kyc-status-filter" />}
    >
      <Table
        head={["Submitted", "Investor", "Country", "Doc", "Number", "Status", ""]}
        rows={items.map((r) => [
          new Date(r.submitted_at).toLocaleString(),
          <div key="u"><div>{r.user_email}</div><div className="text-xs text-neutral-500">{r.full_name}</div></div>,
          r.country,
          r.document_type,
          <span key="n" className="num text-xs">{r.document_number}</span>,
          <Pill key="s" tone={r.status}>{r.status}</Pill>,
          r.status === "pending" ? (
            <div key="ac" className="flex gap-2">
              <button
                data-testid={`admin-kyc-approve-${r.user_id}`}
                onClick={() => decide(r.user_id, "approved")}
                className="text-emerald-400 p-1"
              ><Check size={16} weight="bold" /></button>
              <button
                data-testid={`admin-kyc-reject-${r.user_id}`}
                onClick={() => decide(r.user_id, "rejected")}
                className="text-red-400 p-1"
              ><X size={16} weight="bold" /></button>
            </div>
          ) : (r.decided_by ? <span key="by" className="text-xs text-neutral-500">{r.decided_by}</span> : "—"),
        ])}
      />
    </PanelShell>
  );
}

/* ================================ AUDIT ================================ */
function AuditPanel() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    api.get(`/admin/audit-logs?${q.toString()}`).then((r) => setRows(r.data));
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <PanelShell
      title="Audit logs"
      right={
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-neutral-500" />
          <input
            data-testid="audit-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Actor, action or target…"
            className="bg-black border border-white/20 text-white text-sm pl-9 pr-3 py-2 w-64 focus:border-[#E6C998] focus:outline-none"
          />
        </div>
      }
    >
      <Table
        head={["When", "Actor", "Role", "Action", "Target", "Prev → New"]}
        rows={rows.map((l, i) => [
          new Date(l.created_at).toLocaleString(),
          l.actor_email,
          <span key="r" className="text-xs uppercase tracking-wider text-neutral-400">{l.actor_role}</span>,
          <span key="a" className="text-[#E6C998]">{l.action}</span>,
          <span key="t" className="text-xs num text-neutral-400">{l.target || "—"}</span>,
          <span key="pn" className="text-xs num text-neutral-400 whitespace-pre-wrap">
            {(l.previous_value || l.new_value)
              ? `${JSON.stringify(l.previous_value ?? {})} → ${JSON.stringify(l.new_value ?? {})}`
              : (Object.keys(l.meta || {}).length ? JSON.stringify(l.meta) : "—")}
          </span>,
        ])}
      />
    </PanelShell>
  );
}

/* ========================== INVESTOR DRAWER ============================ */
function InvestorDrawer({ id, onClose }) {
  const { user } = useAuth();
  const can = useCan(user);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newNote, setNewNote] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/investors/${id}`).then((r) => {
      setData(r.data);
      setEditForm({
        name: r.data.investor.name || "",
        phone: r.data.investor.phone || "",
        investment_plan: r.data.investor.investment_plan || "Standard",
      });
    });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end">
        <div className="w-full max-w-3xl bg-[#0A0A0A] border-l border-white/10 p-10 text-neutral-500 text-sm">
          Loading investor…
        </div>
      </div>
    );
  }

  const inv = data.investor;
  const t = data.totals;

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/investors/${id}`, editForm);
      toast.success("Investor updated");
      setEditing(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doSuspend = async () => {
    const reason = window.prompt("Suspension reason:") || null;
    try {
      await api.post(`/admin/investors/${id}/suspend`, { reason });
      toast.success("Account suspended");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doReactivate = async () => {
    try {
      await api.post(`/admin/investors/${id}/reactivate`);
      toast.success("Account reactivated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doPwReset = async () => {
    try {
      const { data } = await api.post(`/admin/investors/${id}/password-reset`);
      navigator.clipboard.writeText(data.reset_link).catch(() => {});
      toast.success("Reset link copied · valid for 60 minutes");
      window.prompt("Send this link to the investor (copied to clipboard):", data.reset_link);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/investors/${id}/notes`, { note: newNote });
      setNewNote("");
      toast.success("Note added");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stats = [
    ["Cash Balance", formatMoney(inv.balance), "gold"],
    ["Reserved", formatMoney(inv.reserved_balance), "gold"],
    ["Total Deposited", formatMoney(t.deposited), "gold"],
    ["Total Withdrawn", formatMoney(t.withdrawn), "gold"],
    ["Total Invested", formatMoney(t.invested), "gold"],
    ["Portfolio Value", formatMoney(data.portfolio.total_value), "gold"],
    ["Unrealised P/L", formatMoney(data.portfolio.total_pl), data.portfolio.total_pl >= 0 ? "up" : "down"],
    ["Bonus Paid", formatMoney(t.bonus_paid), "gold"],
  ];

  const drawerTabs = [
    { k: "overview", label: "Overview" },
    { k: "deposits", label: "Deposits" },
    { k: "withdrawals", label: "Withdrawals" },
    { k: "invests", label: "Investments" },
    { k: "notes", label: "Notes" },
    { k: "actions", label: "Actions" },
  ];

  const filtered = (tp) => data.transactions.filter((x) => x.type === tp);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        data-testid="investor-drawer"
        className="w-full max-w-3xl bg-[#0A0A0A] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10 px-8 py-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="overline">{inv.account_id}</div>
            <div className="font-display text-2xl flex items-center gap-3 mt-1">
              <UserCircle size={24} weight="duotone" className="text-[#E6C998]" />
              {inv.name}
              <Pill tone={inv.account_status}>{inv.account_status}</Pill>
            </div>
            <div className="text-sm text-neutral-400">
              {inv.email}{inv.phone ? ` · ${inv.phone}` : ""}
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white p-1" aria-label="close">
            <X size={22} />
          </button>
        </div>

        <div className="px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {stats.map(([label, value, tone]) => (
              <div key={label} className="border border-white/10 bg-[#141414] p-4">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">{label}</div>
                <div className={`font-display text-xl num ${
                  tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-400" : "text-[#E6C998]"
                }`}>{value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <MetaRow label="Investment Plan" value={inv.investment_plan} />
            <MetaRow label="KYC Status" value={<Pill tone={inv.kyc_status}>{inv.kyc_status}</Pill>} />
            <MetaRow label="Role" value={ROLE_LABEL[inv.role] || inv.role} />
            <MetaRow label="Registered" value={new Date(inv.created_at).toLocaleString()} />
            <MetaRow label="Inclusion (Disability)" value={inv.is_disabled ? "Yes" : "No"} />
            <MetaRow label="Active Positions" value={data.portfolio.holdings.length} />
          </div>

          <div className="flex items-center gap-2 border-b border-white/10 mb-6 overflow-x-auto">
            {drawerTabs.map((t) => (
              <button
                key={t.k}
                data-testid={`inv-tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  tab === t.k
                    ? "border-[#E6C998] text-[#E6C998]"
                    : "border-transparent text-neutral-400 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "overview" && (
            <div className="space-y-6">
              {data.portfolio.holdings.length > 0 && (
                <div>
                  <div className="overline mb-3">Active investments</div>
                  <Table
                    head={["Asset", "Units", "Invested", "Value", "P/L"]}
                    rows={data.portfolio.holdings.map((h) => [
                      <span key="a">{h.asset_name} <span className="text-xs text-neutral-500">{h.asset_symbol}</span></span>,
                      <span key="u" className="num">{h.units}</span>,
                      <span key="i" className="num">{formatMoney(h.invested)}</span>,
                      <span key="v" className="num">{formatMoney(h.current_value)}</span>,
                      <span key="p" className={`num ${h.pl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {formatMoney(h.pl)}
                      </span>,
                    ])}
                  />
                </div>
              )}
              <div>
                <div className="overline mb-3">Latest activity</div>
                <MiniTxList items={data.transactions.slice(0, 8)} />
              </div>
            </div>
          )}

          {tab === "deposits" && <MiniTxList items={filtered("deposit")} showBonus />}
          {tab === "withdrawals" && <MiniTxList items={filtered("withdraw")} showDestination />}
          {tab === "invests" && <MiniTxList items={filtered("invest")} />}

          {tab === "notes" && (
            <div className="space-y-6">
              <div className="border border-white/10 bg-[#141414] p-4">
                <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">Add internal note</label>
                <textarea
                  data-testid="inv-note-input"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  placeholder="Compliance flag, phone call summary, KYC follow-up…"
                  className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none text-sm"
                />
                <button
                  data-testid="inv-note-save"
                  onClick={addNote}
                  className="btn-gold px-5 py-2 rounded-none text-sm mt-3 inline-flex items-center gap-2"
                >
                  <NotePencil size={14} weight="duotone" /> Save note
                </button>
              </div>
              {data.notes.length === 0 ? (
                <div className="text-sm text-neutral-500">No internal notes yet.</div>
              ) : (
                <ul className="space-y-3">
                  {data.notes.map((n) => (
                    <li key={n.id} className="border-l-2 border-[#E6C998]/40 pl-4 py-2">
                      <div className="text-sm whitespace-pre-wrap">{n.note}</div>
                      <div className="text-xs text-neutral-500 mt-1">
                        {n.author_email} · <span className="uppercase tracking-wider">{n.author_role}</span> · {new Date(n.created_at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === "actions" && (
            <div className="space-y-5">
              {!can.manageUsers && (
                <div className="text-xs text-amber-400 flex items-center gap-2">
                  <Warning size={14} weight="duotone" /> Insufficient permissions for some actions.
                </div>
              )}

              {!editing ? (
                <ActionCard
                  icon={NotePencil}
                  title="Update profile"
                  body="Edit name, phone or investment plan. All changes are audit-logged."
                  action={<button data-testid="inv-edit-open" onClick={() => setEditing(true)} className="btn-outline-gold px-4 py-2 text-xs">Edit</button>}
                />
              ) : (
                <div className="border border-[#E6C998]/30 bg-[#141414] p-5 space-y-4">
                  <div className="overline">Edit investor</div>
                  <Field label="Full name">
                    <input data-testid="inv-edit-name" className="lux-input" value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="inv-edit-phone" className="lux-input" value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
                  </Field>
                  <Field label="Investment plan">
                    <select data-testid="inv-edit-plan" className="lux-input" value={editForm.investment_plan}
                      onChange={(e) => setEditForm({ ...editForm, investment_plan: e.target.value })}>
                      {["Standard", "Reserved", "Frontier", "Custody-Only"].map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </Field>
                  <div className="flex gap-3">
                    <button onClick={() => setEditing(false)} className="btn-outline-gold flex-1 py-2 text-sm">Cancel</button>
                    <button data-testid="inv-edit-save" onClick={saveEdit} className="btn-gold flex-1 py-2 text-sm">Save</button>
                  </div>
                </div>
              )}

              <ActionCard
                icon={Key}
                title="Send password reset link"
                body="Generates a secure link valid for 60 minutes. Passwords are never visible to the desk."
                action={
                  <button
                    data-testid="inv-pw-reset"
                    onClick={doPwReset}
                    className="btn-outline-gold px-4 py-2 text-xs inline-flex items-center gap-2"
                  >
                    <Key size={14} weight="duotone" /> Generate
                  </button>
                }
              />

              {inv.account_status === "active" ? (
                <ActionCard
                  icon={PauseCircle}
                  title="Suspend investor"
                  body="Blocks login and all money movement. Reversible."
                  action={
                    <button data-testid="inv-suspend" onClick={doSuspend}
                      className="border border-red-400/40 text-red-400 hover:bg-red-400/10 px-4 py-2 text-xs">
                      Suspend
                    </button>
                  }
                />
              ) : (
                <ActionCard
                  icon={PlayCircle}
                  title="Reactivate investor"
                  body="Restores full account access. Audit-logged."
                  action={
                    <button data-testid="inv-reactivate" onClick={doReactivate}
                      className="btn-gold px-4 py-2 text-xs">
                      Reactivate
                    </button>
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2">
      <span className="text-xs uppercase tracking-wider text-neutral-500">{label}</span>
      <span className="text-sm text-white">{value}</span>
    </div>
  );
}

function ActionCard({ icon: Icon, title, body, action }) {
  return (
    <div className="border border-white/10 bg-[#141414] p-5 flex items-start gap-4">
      <Icon size={22} weight="duotone" className="text-[#E6C998] mt-1 shrink-0" />
      <div className="flex-1">
        <div className="text-sm text-white font-medium">{title}</div>
        <div className="text-xs text-neutral-400 mt-1">{body}</div>
      </div>
      {action}
    </div>
  );
}

function MiniTxList({ items, showBonus, showDestination }) {
  if (!items.length) return <div className="text-sm text-neutral-500">Nothing here.</div>;
  return (
    <div className="border border-white/10 bg-[#141414] overflow-x-auto">
      <table className="w-full text-sm min-w-[600px]">
        <thead>
          <tr className="text-left text-neutral-500 uppercase text-xs tracking-wider">
            <th className="p-4">When</th>
            <th className="p-4">Reference</th>
            <th className="p-4">Method / Asset</th>
            {showDestination && <th className="p-4">Destination</th>}
            <th className="p-4 text-right">Amount</th>
            {showBonus && <th className="p-4 text-right">Bonus</th>}
            <th className="p-4">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id} className="border-t border-white/5 align-top">
              <td className="p-4 text-neutral-400 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
              <td className="p-4 num text-xs">{t.reference || "—"}</td>
              <td className="p-4 text-xs">{t.asset_name || METHOD_LABEL[t.method] || t.method || "—"}</td>
              {showDestination && (
                <td className="p-4 text-xs num truncate max-w-[220px]">
                  {t.destination}{t.beneficiary_name ? ` (${t.beneficiary_name})` : ""}
                </td>
              )}
              <td className="p-4 text-right num">{formatMoney(t.amount)}</td>
              {showBonus && (
                <td className="p-4 text-right num text-[#E6C998]">
                  {t.bonus ? `+${formatMoney(t.bonus)}` : "—"}
                </td>
              )}
              <td className="p-4"><Pill tone={t.status}>{t.status}</Pill></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================ shared UI ============================ */
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">{label}</span>
      {children}
    </label>
  );
}

/* ============================ USDF DECLARATIONS ============================ */
const USDF_STATUSES = [
  ["all", "All"],
  ["received", "Received"],
  ["under_review", "Under review"],
  ["verified", "Verified"],
  ["completed", "Completed"],
  ["rejected", "Rejected"],
];

function UsdfPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/usdf/declarations");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const totalReserved = useMemo(
    () => rows
      .filter((r) => ["verified", "completed"].includes(r.status))
      .reduce((a, r) => a + Number(r.usdf_amount || 0), 0),
    [rows]
  );

  const changeStatus = async (id, status, note) => {
    try {
      await api.post(`/admin/usdf/declarations/${id}/status`, { status, note });
      toast.success(`Declaration marked ${status.replace("_", " ")}`);
      setOpen(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <PanelShell
      title="USDF Declarations"
      right={
        <div className="flex items-center gap-3">
          <div className="text-xs text-neutral-500">
            Reserved · <span className="num text-[#E6C998]">{formatMoney(totalReserved)}</span>
          </div>
          <select
            data-testid="usdf-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-black border border-white/20 text-white px-3 py-2 text-sm"
          >
            {USDF_STATUSES.map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
        </div>
      }
    >
      {loading ? (
        <div className="text-neutral-500 text-sm">Loading declarations…</div>
      ) : (
        <Table
          head={["When", "Holder", "Email", "USDF Amount", "Wallet Ref", "Status", ""]}
          rows={filtered.map((r) => [
            <span key="w" className="text-xs text-neutral-400 whitespace-nowrap">
              {new Date(r.created_at).toLocaleString()}
            </span>,
            <span key="h" className="text-white">{r.holder_name}</span>,
            <span key="e" className="text-xs num">{r.holder_email}</span>,
            <span key="a" className="num text-[#E6C998]">{formatMoney(r.usdf_amount)}</span>,
            <span key="r" className="text-xs num truncate inline-block max-w-[180px]" title={r.wallet_reference}>
              {r.wallet_reference}
            </span>,
            <Pill key="s" tone={r.status}>{(r.status || "").replace("_", " ")}</Pill>,
            <button
              key="o"
              data-testid={`usdf-open-${r.id}`}
              onClick={() => setOpen(r)}
              className="text-xs text-[#E6C998] gold-underline"
            >
              View
            </button>,
          ])}
        />
      )}

      {open && (
        <UsdfDrawer
          dec={open}
          onClose={() => setOpen(null)}
          onStatus={(status, note) => changeStatus(open.id, status, note)}
        />
      )}
    </PanelShell>
  );
}

function UsdfDrawer({ dec, onClose, onStatus }) {
  const [note, setNote] = useState(dec.admin_note || "");
  return (
    <div
      data-testid="usdf-drawer"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl h-full bg-[#0A0A0A] border-l border-[#E6C998]/25 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="overline mb-1">Declaration · {dec.id?.slice(-10)}</div>
            <div className="font-display text-2xl">{dec.holder_name}</div>
          </div>
          <button data-testid="usdf-drawer-close" onClick={onClose} className="text-neutral-400 hover:text-white p-2">
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-5 text-sm">
          <Row k="Email" v={dec.holder_email} />
          <Row k="USDF amount" v={<span className="num text-[#E6C998]">{formatMoney(dec.usdf_amount)}</span>} />
          <Row k="Wallet / Account reference" v={<span className="font-mono text-xs break-all">{dec.wallet_reference}</span>} />
          <Row k="Declaration date" v={dec.declaration_date || "—"} />
          <Row k="Signature" v={<span style={{ fontFamily: "Playfair Display, serif" }} className="italic text-[#E6C998]">{dec.signature}</span>} />
          <Row k="Submitted" v={new Date(dec.created_at).toLocaleString()} />
          <Row k="Client IP" v={<span className="font-mono text-xs">{dec.client_ip || "—"}</span>} />
          <Row k="Current status" v={<Pill tone={dec.status}>{(dec.status || "").replace("_", " ")}</Pill>} />
          {dec.note && (
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Holder note</div>
              <div className="border border-white/10 bg-black p-3 text-neutral-300 whitespace-pre-wrap">{dec.note}</div>
            </div>
          )}

          <div className="divider-gold my-6" />

          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Admin note (optional)</div>
            <textarea
              data-testid="usdf-admin-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full bg-black border border-white/20 text-white px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="usdf-mark-under-review"
              onClick={() => onStatus("under_review", note)}
              className="btn-outline-gold py-3 text-xs"
            >
              Under review
            </button>
            <button
              data-testid="usdf-mark-verified"
              onClick={() => onStatus("verified", note)}
              className="btn-outline-gold py-3 text-xs"
            >
              Verified
            </button>
            <button
              data-testid="usdf-mark-completed"
              onClick={() => onStatus("completed", note)}
              className="btn-gold py-3 text-xs"
            >
              Mark completed
            </button>
            <button
              data-testid="usdf-mark-rejected"
              onClick={() => onStatus("rejected", note)}
              className="py-3 text-xs border border-red-400/40 text-red-400 hover:bg-red-400/10"
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-white/5 pb-3">
      <div className="text-xs uppercase tracking-wider text-neutral-500 shrink-0 pt-1">{k}</div>
      <div className="text-right text-sm text-white break-words">{v}</div>
    </div>
  );
}

function PanelShell({ title, right, children }) {
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <h2 className="font-display text-2xl">{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function Table({ head, rows }) {
  if (!rows.length)
    return <div className="border border-dashed border-white/10 p-10 text-center text-neutral-500 text-sm">Nothing here yet.</div>;
  return (
    <div className="border border-white/10 bg-[#141414] overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead>
          <tr className="text-left text-neutral-500 uppercase text-xs tracking-wider">
            {head.map((h) => <th key={h} className="p-4">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/5 align-top">
              {r.map((c, j) => <td key={j} className="p-4">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pill({ tone, children }) {
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 border ${STATUS_TONE[tone] || "text-neutral-400 border-white/15"}`}>
      {children}
    </span>
  );
}

function FinancialStatusFilter({ value, onChange, includeAll }) {
  const opts = [
    ["pending", "Pending"],
    ["completed", "Approved / Completed"],
    ["rejected", "Rejected"],
  ];
  return (
    <Select
      value={value}
      onChange={onChange}
      options={includeAll ? [["", "All statuses"], ...opts] : opts}
      testid="status-filter"
    />
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-black border border-white/20 text-white text-sm px-3 py-2 focus:border-[#E6C998] focus:outline-none"
    >
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );
}

function ExportButton({ type, status }) {
  const download = async () => {
    try {
      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/admin/transactions/export.csv`);
      if (type) url.searchParams.set("type", type);
      if (status) url.searchParams.set("status", status);
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = `madrefajona-transactions-${Date.now()}.csv`;
      document.body.appendChild(dl); dl.click(); dl.remove();
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };
  return (
    <button
      data-testid="tx-export"
      onClick={download}
      className="btn-outline-gold px-4 py-2 text-xs inline-flex items-center gap-2"
    >
      <DownloadSimple size={14} weight="duotone" /> Export CSV
    </button>
  );
}

/* =========================== PAYMENT METHODS =========================== */
function PaymentMethodsPanel() {
  const { user } = useAuth();
  const canEdit = FINANCE_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/payment-methods").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (payload, isNew) => {
    try {
      if (isNew) await api.post("/admin/payment-methods", payload);
      else await api.put(`/admin/payment-methods/${payload.key}`, payload);
      toast.success("Payment method saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete payment method "${key}"? Investors will no longer be able to use it.`)) return;
    try {
      await api.delete(`/admin/payment-methods/${key}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const typeIcon = (t) => t === "crypto" ? CurrencyBtc : t === "bank" ? Bank : Coins;

  return (
    <PanelShell
      title={`Payment methods · ${rows.length}`}
      right={canEdit && (
        <button
          data-testid="pm-add"
          onClick={() => setEditing({ __new: true, method_type: "bank", active: true, instructions: {} })}
          className="btn-outline-gold px-4 py-2 text-xs inline-flex items-center gap-2"
        >
          <Plus size={14} weight="bold" /> Add method
        </button>
      )}
    >
      <p className="text-xs text-neutral-500 mb-4 max-w-2xl">
        Wallet addresses and bank details shown to investors when they submit a
        deposit. Edits take effect immediately for new requests. Existing pending
        deposits keep the details they were issued with.
      </p>
      <Table
        head={["Key", "Type", "Label", "ETA", "Fields", "Active", ""]}
        rows={rows.map((m) => {
          const Icon = typeIcon(m.method_type);
          return [
            <span key="k" className="num text-xs">{m.key}</span>,
            <span key="t" className="capitalize flex items-center gap-2">
              <Icon size={14} weight="duotone" className="text-[#E6C998]" /> {m.method_type}
            </span>,
            m.label,
            <span key="e" className="text-xs text-neutral-400">{m.eta || "—"}</span>,
            <span key="f" className="text-xs text-neutral-400 num">
              {Object.keys(m.instructions || {}).length}
            </span>,
            <Pill key="a" tone={m.active ? "active" : "suspended"}>
              {m.active ? "active" : "disabled"}
            </Pill>,
            canEdit ? (
              <div key="ac" className="flex gap-2">
                <button
                  data-testid={`pm-edit-${m.key}`}
                  onClick={() => setEditing(m)}
                  className="text-[#E6C998] p-1"
                  title="Edit"
                ><NotePencil size={16} weight="duotone" /></button>
                <button
                  data-testid={`pm-delete-${m.key}`}
                  onClick={() => remove(m.key)}
                  className="text-red-400 p-1"
                  title="Delete"
                ><Trash size={16} weight="duotone" /></button>
              </div>
            ) : "—",
          ];
        })}
      />

      {editing && (
        <PaymentMethodEditor
          method={editing}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </PanelShell>
  );
}

function PaymentMethodEditor({ method, onSave, onClose }) {
  const isNew = !!method.__new;
  const [key, setKey] = useState(method.key || "");
  const [label, setLabel] = useState(method.label || "");
  const [eta, setEta] = useState(method.eta || "");
  const [note, setNote] = useState(method.note || "");
  const [methodType, setMethodType] = useState(method.method_type || "bank");
  const [active, setActive] = useState(method.active ?? true);
  const [pairs, setPairs] = useState(
    Object.entries(method.instructions || {}).map(([k, v]) => ({ k, v: String(v) }))
  );

  const addRow = () => setPairs([...pairs, { k: "", v: "" }]);
  const removeRow = (i) => setPairs(pairs.filter((_, j) => j !== i));

  const submit = () => {
    if (isNew && !/^[a-z0-9_]{2,32}$/.test(key)) {
      toast.error("Key must be 2-32 chars, lowercase letters, digits or underscores");
      return;
    }
    if (!label.trim()) { toast.error("Label is required"); return; }
    const instructions = {};
    for (const p of pairs) {
      const kk = p.k.trim();
      if (kk) instructions[kk] = p.v;
    }
    onSave({
      key, label, eta: eta || null, note: note || null,
      method_type: methodType, active, instructions,
    }, isNew);
  };

  const preset = methodType === "bank"
    ? ["beneficiary", "bank", "iban", "swift", "address"]
    : ["network", "address", "memo"];

  const fillPreset = () => {
    const have = new Set(pairs.map((p) => p.k));
    setPairs([
      ...pairs,
      ...preset.filter((k) => !have.has(k)).map((k) => ({ k, v: "" })),
    ]);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="pm-editor"
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-[#E6C998]/30 w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto"
      >
        <div className="overline mb-2">{isNew ? "Add payment method" : "Edit payment method"}</div>
        <h3 className="font-display text-2xl mb-6">
          {isNew ? "New payment method" : `Edit ${label || key}`}
        </h3>

        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Key (unique)">
              <input
                data-testid="pm-key"
                className="lux-input num"
                value={key}
                disabled={!isNew}
                onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                placeholder="btc, bank_wire, eth_erc20…"
              />
            </Field>
            <Field label="Type">
              <select
                data-testid="pm-type"
                className="lux-input"
                value={methodType}
                onChange={(e) => setMethodType(e.target.value)}
              >
                <option value="bank">Bank / Wire transfer</option>
                <option value="crypto">Crypto wallet</option>
              </select>
            </Field>
          </div>

          <Field label="Investor-facing label">
            <input
              data-testid="pm-label"
              className="lux-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Bank Wire (SWIFT)"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="ETA">
              <input
                data-testid="pm-eta"
                className="lux-input"
                value={eta}
                onChange={(e) => setEta(e.target.value)}
                placeholder="1-2 business days"
              />
            </Field>
            <label className="flex items-end gap-2 pb-3 text-sm">
              <input
                data-testid="pm-active"
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="accent-[#E6C998] w-4 h-4"
              />
              Active — visible to investors
            </label>
          </div>

          <Field label="Note (optional, shown to investor)">
            <textarea
              data-testid="pm-note"
              className="lux-input"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Send only USDT on Ethereum. Other networks will be lost."
            />
          </Field>

          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-neutral-400">
                Instructions ({pairs.length})
              </span>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={fillPreset}
                  className="text-xs text-neutral-400 hover:text-white"
                >
                  Suggest {methodType} fields
                </button>
                <button
                  type="button"
                  onClick={addRow}
                  data-testid="pm-add-row"
                  className="text-xs text-[#E6C998] flex items-center gap-1"
                >
                  <Plus size={14} weight="bold" /> Add field
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {pairs.length === 0 && (
                <div className="text-xs text-neutral-500 border border-dashed border-white/10 p-4">
                  No instruction fields yet — for a bank wire add iban/swift/beneficiary,
                  for a crypto wallet add network/address.
                </div>
              )}
              {pairs.map((p, i) => (
                <div key={i} className="grid grid-cols-[160px_1fr_auto] gap-2 items-center">
                  <input
                    data-testid={`pm-row-key-${i}`}
                    placeholder="iban, address…"
                    className="lux-input num text-xs"
                    value={p.k}
                    onChange={(e) => setPairs(pairs.map((x, j) => j === i ? { ...x, k: e.target.value } : x))}
                  />
                  <input
                    data-testid={`pm-row-val-${i}`}
                    placeholder="value shown to investor"
                    className="lux-input num text-xs"
                    value={p.v}
                    onChange={(e) => setPairs(pairs.map((x, j) => j === i ? { ...x, v: e.target.value } : x))}
                  />
                  <button
                    onClick={() => removeRow(i)}
                    className="text-red-400 p-2"
                    title="Remove"
                  ><Trash size={14} weight="duotone" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-white/10">
            <button onClick={onClose} className="btn-outline-gold flex-1 py-3 text-sm">Cancel</button>
            <button data-testid="pm-save" onClick={submit} className="btn-gold flex-1 py-3 text-sm">
              {isNew ? "Create method" : "Save changes"}
            </button>
          </div>
          <p className="text-xs text-neutral-500">
            Every change is audit-logged with the previous and new values.
          </p>
        </div>
      </div>
    </div>
  );
}
